import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { PatientPrivateBillListItem } from "@/Patient/Models/History/PatientPrivateBillListItem";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { IPatientPrivateBillModel } from "@/Patient/Models/UI/History/IPatientPrivateBillModel";
import { PrivateBillEdit } from "./PrivateBillEdit";
import { PrivateBillView } from "./PrivateBillView";

import styles from "./PrivateBillControl.module.scss";

interface IPatientHistoryPrivateBillsProps {
    Model: IPatientPrivateBillModel;
    BillableServicesModel: IBillableService[];
    OnCloseOperation?: number;
    OnCancelBillOp?: number;
    DisplayFullWidth?: boolean;
    DoNotRenderAsScrollable?: boolean
    PatientModel: Patient;

    PrintOp: number;
    CloseInvalidOp: number;
    SaveInvalidationOp: number;
    InvalidateOp: number;
    InvalidationChangeOp: number;

    IsPopup: boolean;

    DisableButtons: boolean;
    OnButtonClick: (callback: () => void) => void;
}

class PatientHistoryPrivateBillsState {
    public disableButtons: boolean = false;
}

class PrivateBillControl extends BaseReactComponent<IPatientHistoryPrivateBillsProps, PatientHistoryPrivateBillsState> {
    constructor(props: IPatientHistoryPrivateBillsProps) {
        super(props);
        this.state = new PatientHistoryPrivateBillsState();
    }

    public componentDidUpdate(prevProps: IPatientHistoryPrivateBillsProps): void {
        $(window).trigger("resize");

        if (
            this.props.Model.get("PrivateBillId") !== prevProps.Model.get("PrivateBillId")
            || this.props.Model.get("IsClosed") !== prevProps.Model.get("IsClosed")
            || this.props.Model.get("IsNewBill") !== prevProps.Model.get("IsNewBill")
            || this.props.Model.get("IsEditBill") !== prevProps.Model.get("IsEditBill")
        ) {
            this.mutateState(s => s.disableButtons = false);
        }
    }

    public componentDidMount(): void {
        window.setTimeout(() => {
            sessionStore.dispatch(LocalPatientOperations.History.PrivateBill_LoadPatientPrivateBill);
        }, 0);
    }

    public render(): JSX.Element {
        var privateBillModel = this.props.Model;
        return (
            <div>
                <div className="medical-summary-container">
                    <div className="tb medical-summary">
                        {this.renderLeftSide(privateBillModel)}
                        {privateBillModel.get("ShowDetailsOrNewBill") === true && this.renderRightSide(privateBillModel)}
                        {privateBillModel.get("ShowDetailsOrNewBill") === false && this.renderEmptyRightSide()}
                    </div>
                </div>
            </div>
        );
    }

    private renderLeftSide(model: IPatientPrivateBillModel): JSX.Element {
        return (
            <div className="fxheight tbc full-border left-primary">
                <div className="note-search">
                    <div className="header" />
                    <div className="search-field">
                        <div className="gf">
                            <span className="glyphicon glyphicon-search" />
                        </div>
                        <TextboxControl
                            debounceMs={400}
                            value={model.get("SearchText")}
                            className="fullWidth"
                            operation={LocalPatientOperations.History.PrivateBill_SearchText}
                            placeholder={cacheStore.GetString("SearchLabel")}
                            dataCy="privateBill-search"
                        />
                    </div>
                    {model.get("IsCriteriaExpanded") === true && (
                        <div style={{ marginTop: "10px" }}>
                            <MultiDateSelector
                                id="doc_search_date_selector"
                                fullWidth={true}
                                fromMyleOperation={LocalPatientOperations.History.PrivateBill_FromDateChanged}
                                toMyleOperation={LocalPatientOperations.History.PrivateBill_ToDateChanged}
                                fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                from={model.get("DateFrom")}
                                to={model.get("DateTo")}
                            />
                        </div>
                    )}
                    <div className="clearfix" style={{ marginTop: "15px" }}>
                        <a onClick={this.CreateNewBill.bind(this)} data-cy="privateBill-newBill">{cacheStore.GetString("NewBillLabel")}</a>
                        {model.get("IsCriteriaExpanded") === true && <a className="pull-right" onClick={this.ShowCriteria.bind(this)}>[-]{cacheStore.GetString("CriteriaLabel")}</a>}
                        {model.get("IsCriteriaExpanded") === false && <a className="pull-right" onClick={this.ShowCriteria.bind(this)}>[+]{cacheStore.GetString("CriteriaLabel")}</a>}
                    </div>
                </div>
                {this.renderPrivateBillList(model)}
            </div>
        );
    }

    private renderPrivateBillList(model: IPatientPrivateBillModel): JSX.Element {
        let privateBillList = model?.get("PrivateBillList") ?? [];

        let privateBillRender = privateBillList.map((privateBill: PatientPrivateBillListItem) => {
            let portalSyncButton: JSX.Element = null;

            if (cacheStore.ClinicSettings_Portal.EnablePortalWebsite && (cacheStore.ClinicSettings.EnablePortalInvitationFlowV2 || this.props.PatientModel?.IsRegisteredToPortal())) {
                const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(
                    privateBill.Model
                );

                portalSyncButton = (
                    <PortalStatusButton
                        className="push-left-5"
                        onClick={e => this.PortalStatusClicked(privateBill, e)}
                        {...buttonProps}
                    />
                );
            }

            const isSelected = (this.props.Model.get("PrivateBillId") != null && this.props.Model.get("PrivateBillId") == privateBill.Model.PrivateBillId);
            let selectedClass = "med-note";
            if (isSelected) selectedClass += " selected";

            let titleStyle = "";
            let labelStyle = "";

            if (privateBill.Model.IsInvalid) {
                titleStyle = styles.textErrorBold;
                labelStyle = styles.textError;
            }
            else if (privateBill.Model.IsClosed) {
                titleStyle = styles.textBold;
                labelStyle = styles.textBold;
            }
            else if (!privateBill.Model.IsClosed && !privateBill.Model.IsLockedForEditing) {
                titleStyle = styles.textItalic;
                labelStyle = styles.textItalic;
            }

            return (
                <div
                    className="pointer"
                    onClick={this.SetSelectedPrivateBill.bind(this, privateBill)}
                    key={privateBill.Model.PrivateBillId}
                >
                    <div className={selectedClass + " " + styles.selectedClassGrid}>
                        <p className={titleStyle}>{privateBill.Model.Employee}</p>
                        <p className={labelStyle + " " + styles.justifySelfEnd}>{privateBill.Model.BillDate}</p>
                        <p className={labelStyle}>
                            {!cacheStore.ClinicSettings.EnablePrivateBillRecipient && (
                                `${cacheStore.GetString("BilledByLabel")} ${privateBill.Model.CreatedBy}`
                            )}
                            {cacheStore.ClinicSettings.EnablePrivateBillRecipient && privateBill.Model.BilledTo === PrivateBill_BilledTo.ThirdParty
                                && !IsNullOrWhiteSpace(privateBill.Model.RecipientName) && (
                                `${cacheStore.GetString("BilledToLabel")} ${privateBill.Model.RecipientName}`
                            )
                            }
                        </p>
                        <p className={styles.justifySelfEnd}>{portalSyncButton}</p>
                        <p className={labelStyle}>#{privateBill.Model.BillNumber.toString()}</p>
                        <p className={labelStyle + " " + styles.justifySelfEnd}>$ {privateBill.Model.TotalBill.toFixed(2)}</p>
                    </div>
                </div>
            );
        });

        return (
            <div data-hvar="40" className="note-collection scrollable" data-cy="privateBill-noteCollection">
                {privateBillRender}
            </div>
        );
    }

    private renderRightSide(model: IPatientPrivateBillModel): JSX.Element {
        let editMode = (model.get("IsNewBill") === true || model.get("IsEditBill") === true);
        return (
            <div className="tbc right-primary">
                {editMode && (
                    <PrivateBillEdit
                        Model={this.props.Model}
                        BillableServicesModel={this.props.BillableServicesModel}
                        IsPopup={false}
                        PrintOp={LocalPatientOperations.History.PrivateBill_Print}
                        CloseInvalidOp={LocalPatientOperations.History.PrivateBill_CloseInvalidPopup}
                        SaveInvalidationOp={LocalPatientOperations.History.PrivateBill_SaveInvalidation}
                        InvalidateOp={LocalPatientOperations.History.PrivateBill_InvalidateBill}
                        InvalidationChangeOp={LocalPatientOperations.History.PrivateBill_NoteInvalidationChange}
                        PatientModel={this.props.PatientModel}
                        DisableButtons={this.state.disableButtons}
                        OnButtonClick={callback => this.disableButtons(callback)}
                    />
                )}
                {!editMode && (
                    <PrivateBillView
                        Model={this.props.Model}
                        BillableServicesModel={this.props.BillableServicesModel}
                        IsPopup={false}
                        PrintOp={LocalPatientOperations.History.PrivateBill_Print}
                        CloseInvalidOp={LocalPatientOperations.History.PrivateBill_CloseInvalidPopup}
                        SaveInvalidationOp={LocalPatientOperations.History.PrivateBill_SaveInvalidation}
                        InvalidateOp={LocalPatientOperations.History.PrivateBill_InvalidateBill}
                        InvalidationChangeOp={LocalPatientOperations.History.PrivateBill_NoteInvalidationChange}
                        PatientModel={this.props.PatientModel}
                        DisableButtons={this.state.disableButtons}
                        OnButtonClick={callback => this.disableButtons(callback)}
                    />
                )}
            </div>
        );
    }

    private renderEmptyRightSide(): JSX.Element {
        return (
            <div className="tbc right-primary">
                <div className="scrollable">
                    <div className="summary-container no-border" />
                </div>
            </div>
        );
    }

    private CreateNewBill(): void {
        if (this.props.Model.get("IsEditBill") === false)
            LocalEvent.Dispatch(LocalPatientOperations.History.PrivateBill_CreateNewBill);
        else {
            sessionStore.showPopup({
                title: cacheStore.GetString("EditBillTitleLabel"),
                content: cacheStore.GetString("BillEditLabel"),
                showCancel: true,

                okLabel: cacheStore.GetString("ContinueLabel"),
                okAction: () => LocalEvent.Dispatch(LocalPatientOperations.History.PrivateBill_CreateNewBill)
            });
        }
    }

    private ShowCriteria(): void {
        LocalEvent.Dispatch(LocalPatientOperations.History.PrivateBill_CriteriaClick);
    }

    private SetSelectedPrivateBill(privateBill: PatientPrivateBillListItem): void {
        if (privateBill.Model.PrivateBillId === this.props.Model.get("PrivateBillId"))
            return;

        this.mutateState(
            s => s.disableButtons = true,
            () => {
                sessionStore.dispatch(
                    LocalPatientOperations.History.PrivateBill_ShowSelectedPrivateBill,
                    { privateBill }
                );
            }
        );
    }

    private PortalStatusClicked(privateBill: PatientPrivateBillListItem, e: React.MouseEvent): void {
        e.stopPropagation();

        var operation = new LocalEvent(LocalPatientOperations.History.PrivateBill_PortalStatusClicked);
        operation.AddItem("bill", privateBill.Model);
        LocalEventDispatcher.dispatch(operation);
    }

    private disableButtons(callback: () => void): void {
        this.mutateState(
            s => s.disableButtons = true,
            () => { callback(); }
        );
    }
}

export { IPatientHistoryPrivateBillsProps, PrivateBillControl };
